import React from 'react'
import { Field, ErrorMessage } from 'formik'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

export const CenterMembershipAgreementForm: React.FC = () => {
  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-2xl leading-6 font-bold text-[#3C2416]">
          Membership Agreement & Signature
        </h3>
        <p className="mt-2 text-sm text-[#142841]">
          Please review and agree to the NASCC membership terms and provide your
          digital signature to complete the application.
        </p>
      </div>

      <div className="px-4 py-5 sm:px-6 space-y-8">
        {/* Agreement Requirements */}
        <div>
          <h4 className="text-lg font-semibold text-[#3C2416] mb-4">
            Membership Requirements
          </h4>

          <div className="space-y-6">
            {/* GRNDAD Agreement */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start">
                <Field
                  type="checkbox"
                  name="agreeToGrndad"
                  id="agreeToGrndad"
                  className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded mt-1"
                />
                <div className="ml-3 flex-1">
                  <label
                    htmlFor="agreeToGrndad"
                    className="text-sm font-medium text-[#3C2416] cursor-pointer"
                  >
                    Agreement to participate in GRNDAD (Globin Research Network
                    for Data and Discovery) *
                  </label>
                  <p className="mt-2 text-sm text-[#142841]">
                    I agree that our center will participate in the Globin
                    Research Network for Data and Discovery (GRNDAD), which
                    includes sharing de-identified patient data for research
                    purposes and quality improvement initiatives. This
                    participation is essential for NASCC membership and helps
                    advance sickle cell disease care through collaborative
                    research and data sharing.
                  </p>
                  <div className="mt-2 flex items-center text-sm text-amber-700 bg-amber-50 p-2 rounded">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                    This agreement is required for membership.
                  </div>
                </div>
              </div>
              <ErrorMessage
                name="agreeToGrndad"
                component="p"
                className="mt-2 text-sm text-red-600"
              />
            </div>

            {/* Bylaws Agreement */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start">
                <Field
                  type="checkbox"
                  name="agreeToBylaws"
                  id="agreeToBylaws"
                  className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded mt-1"
                />
                <div className="ml-3 flex-1">
                  <label
                    htmlFor="agreeToBylaws"
                    className="text-sm font-medium text-[#3C2416] cursor-pointer"
                  >
                    Agreement to NASCC Bylaws and Governance *
                  </label>
                  <p className="mt-2 text-sm text-[#142841]">
                    I agree to abide by the NASCC bylaws, governance structure,
                    and membership obligations. This includes participating in
                    NASCC activities, maintaining center standards, and
                    supporting the organization&apos;s mission to improve sickle
                    cell disease care through collaboration and excellence.
                  </p>
                  <div className="mt-2 flex items-center text-sm text-amber-700 bg-amber-50 p-2 rounded">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                    This agreement is required for membership.
                  </div>
                </div>
              </div>
              <ErrorMessage
                name="agreeToBylaws"
                component="p"
                className="mt-2 text-sm text-red-600"
              />
            </div>
          </div>
        </div>

        {/* Digital Signature Section */}
        <div className="border-t border-gray-200 pt-6">
          <h4 className="text-lg font-semibold text-[#3C2416] mb-4">
            Digital Signature
          </h4>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="signedBy"
                className="block text-sm font-medium text-[#3C2416] mb-2"
              >
                Full Name (Digital Signature) *
              </label>
              <Field
                type="text"
                name="signedBy"
                id="signedBy"
                className="block w-full max-w-md rounded-lg border-0 py-3 px-4 text-[#142841] shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm sm:leading-6 transition-all duration-200"
                placeholder="Enter your full name"
              />
              <p className="mt-2 text-sm text-gray-500">
                By typing your full name above, you are providing a digital
                signature and certifying that:
              </p>
              <ul className="mt-2 text-sm text-gray-500 list-disc list-inside space-y-1">
                <li>
                  You are authorized to submit this application on behalf of
                  your center
                </li>
                <li>
                  All information provided in this application is accurate and
                  complete
                </li>
                <li>You agree to the terms and conditions outlined above</li>
                <li>
                  You understand that any false statements may result in
                  rejection of this application
                </li>
              </ul>
              <ErrorMessage
                name="signedBy"
                component="p"
                className="mt-2 text-sm text-red-600"
              />
            </div>
          </div>
        </div>

        {/* Submission Information */}
        <div className="border-t border-gray-200 pt-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h5 className="text-sm font-medium text-green-800 mb-2">
                  What happens after submission?
                </h5>
                <div className="text-sm text-green-700 space-y-2">
                  <p>
                    • Your application will be reviewed by the NASCC membership
                    committee
                  </p>
                  <p>
                    • You will receive a confirmation email shortly after
                    submission
                  </p>
                  <p>• The review process typically takes 2-4 weeks</p>
                  <p>• You will be notified of the decision via email</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Required Fields Notice */}
        <div className="border-t border-gray-200 pt-6">
          <p className="text-sm text-gray-500">
            Fields marked with an asterisk (*) are required to complete your
            application.
          </p>
        </div>
      </div>
    </div>
  )
}
